import { SEOHead } from '@/components/seo-head'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { 
  Search, 
  Building2, 
  Shield, 
  Users, 
  Star,
  ArrowRight,
  Globe,
  FileText,
  MessageSquare,
  TrendingUp,
  Award,
  Clock,
  Filter,
  Zap
} from 'lucide-react'

export default function FindSuppliers() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Find Suppliers - SourcedIn B2B Platform",
    "description": "Discover qualified suppliers and vendors for your business needs. Connect with verified B2B partners through our AI-powered supplier discovery platform.",
    "url": "https://sourcedin.ai/find-suppliers",
    "mainEntity": {
      "@type": "Organization",
      "name": "SourcedIn",
      "description": "AI-powered B2B supplier discovery and RFP management platform"
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://sourcedin.ai"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Find Suppliers",
          "item": "https://sourcedin.ai/find-suppliers"
        }
      ]
    }
  }

  return (
    <>
      <SEOHead
        title="Find Suppliers - AI-Powered B2B Supplier Discovery Platform"
        description="Discover qualified suppliers and vendors for your business. Connect with verified B2B partners through our AI-powered supplier discovery platform. Streamline procurement with intelligent matching."
        keywords="find suppliers, supplier discovery, B2B vendors, qualified suppliers, vendor search, procurement platform, supplier database, business suppliers, vendor matching, supplier network"
        canonical="https://sourcedin.ai/find-suppliers"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 flex items-center">
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-primary/10 text-primary border border-primary/20 rounded-full text-sm font-medium mb-6">
              <Search className="w-4 h-4 mr-2" />
              AI-Powered Supplier Discovery
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Find the Perfect
              <span className="text-primary block">Suppliers for Your Business</span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Discover qualified suppliers and vendors instantly with our AI-powered platform. 
              Connect with verified B2B partners, streamline procurement, and make informed decisions faster.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button size="lg" className="text-lg px-8 py-4">
                Start Finding Suppliers
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button variant="outline" size="lg" className="text-lg px-8 py-4">
                View Supplier Network
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-2">10,000+</div>
                <div className="text-gray-600">Verified Suppliers</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-2">50+</div>
                <div className="text-gray-600">Industries Covered</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-2">95%</div>
                <div className="text-gray-600">Match Accuracy</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              How Supplier Discovery Works
            </h2>
            <p className="text-xl text-gray-600">
              Our AI-powered platform makes finding the right suppliers simple and efficient
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="text-center border-2 hover:border-primary/50 transition-colors">
              <CardHeader>
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-primary" />
                </div>
                <CardTitle>1. Define Your Needs</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Specify your requirements, budget, timeline, and quality standards. 
                  Our AI understands your procurement needs.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-2 hover:border-primary/50 transition-colors">
              <CardHeader>
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-primary" />
                </div>
                <CardTitle>2. AI Matching</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Our intelligent algorithm matches you with qualified suppliers 
                  based on your specific criteria and requirements.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-2 hover:border-primary/50 transition-colors">
              <CardHeader>
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-primary" />
                </div>
                <CardTitle>3. Connect & Collaborate</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Connect directly with matched suppliers, review their profiles, 
                  and start meaningful business conversations.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Powerful Supplier Discovery Features
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to find and connect with the right suppliers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <Building2 className="w-8 h-8 text-primary mb-4" />
                <CardTitle>Verified Suppliers</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Access a database of verified suppliers with complete profiles, 
                  certifications, and quality ratings.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <Filter className="w-8 h-8 text-primary mb-4" />
                <CardTitle>Advanced Filtering</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Filter suppliers by location, industry, size, certifications, 
                  and other specific criteria.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <TrendingUp className="w-8 h-8 text-primary mb-4" />
                <CardTitle>Market Insights</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Get market trends, pricing insights, and supplier performance 
                  analytics to make informed decisions.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <Shield className="w-8 h-8 text-primary mb-4" />
                <CardTitle>Quality Assurance</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  All suppliers undergo verification processes including 
                  business registration, quality certifications, and reviews.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <Globe className="w-8 h-8 text-primary mb-4" />
                <CardTitle>Global Network</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Connect with suppliers worldwide or focus on local partners 
                  based on your geographic preferences.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <FileText className="w-8 h-8 text-primary mb-4" />
                <CardTitle>Document Management</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Securely share and manage procurement documents, 
                  contracts, and specifications with suppliers.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Find Suppliers Across Industries
            </h2>
            <p className="text-xl text-gray-600">
              Our platform covers suppliers in every major industry and sector
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6 max-w-5xl mx-auto">
            {[
              'Manufacturing', 'Technology', 'Healthcare', 'Construction',
              'Food & Beverage', 'Automotive', 'Aerospace', 'Energy',
              'Retail', 'Finance', 'Education', 'Logistics'
            ].map((industry) => (
              <Card key={industry} className="text-center hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <Building2 className="w-8 h-8 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold text-gray-900">{industry}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-primary/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why Choose SourcedIn for Supplier Discovery?
            </h2>
            <p className="text-xl text-gray-600">
              Join thousands of businesses that trust our platform for their procurement needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="space-y-6">
              {[
                {
                  icon: <Clock className="w-6 h-6 text-primary" />,
                  title: "Save Time",
                  description: "Reduce supplier search time by 80% with AI-powered matching"
                },
                {
                  icon: <Award className="w-6 h-6 text-primary" />,
                  title: "Quality Suppliers",
                  description: "Access pre-verified suppliers with proven track records"
                },
                {
                  icon: <TrendingUp className="w-6 h-6 text-primary" />,
                  title: "Better ROI",
                  description: "Find cost-effective suppliers that meet your quality standards"
                }
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    {benefit.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-6">
              {[
                {
                  icon: <Users className="w-6 h-6 text-primary" />,
                  title: "Network Effect",
                  description: "Leverage our growing network of suppliers and buyers"
                },
                {
                  icon: <Shield className="w-6 h-6 text-primary" />,
                  title: "Secure Platform",
                  description: "Enterprise-grade security for all your procurement data"
                },
                {
                  icon: <MessageSquare className="w-6 h-6 text-primary" />,
                  title: "Direct Communication",
                  description: "Connect directly with suppliers through our platform"
                }
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    {benefit.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              What Our Users Say
            </h2>
            <p className="text-xl text-gray-600">
              Discover how businesses are transforming their procurement process
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                quote: "SourcedIn helped us find three qualified suppliers in just one day. The AI matching was incredibly accurate.",
                author: "Sarah Johnson",
                role: "Procurement Manager",
                company: "TechCorp Industries"
              },
              {
                quote: "The platform's verification process gave us confidence in our supplier choices. Highly recommended!",
                author: "Michael Chen",
                role: "Operations Director",
                company: "Global Manufacturing Co."
              },
              {
                quote: "We've reduced our supplier search time by 75% while improving the quality of our partnerships.",
                author: "Emily Rodriguez",
                role: "Supply Chain Manager",
                company: "Innovation Solutions"
              }
            ].map((testimonial, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4 italic">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.author}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}, {testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary/80 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">
              Ready to Find Your Perfect Suppliers?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Join thousands of businesses using SourcedIn to streamline their procurement process
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" className="text-lg px-8 py-4">
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-primary">
                Schedule Demo
              </Button>
            </div>
            <p className="text-sm mt-6 opacity-75">
              No credit card required • 14-day free trial • Cancel anytime
            </p>
          </div>
        </div>
      </section>
    </>
  )
}
